import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Retorna a lista de cidades distintas (em ordem alfabética) cadastradas em `clientes`
 * para um determinado estado (UF). Quando `estado` é vazio/nulo, retorna `[]`.
 */
export function useCidadesPorEstado(estado?: string | null) {
  const uf = (estado || "").trim().toUpperCase();

  return useQuery({
    queryKey: ["clientes", "cidades-por-estado", uf],
    queryFn: async () => {
      if (!uf) return [] as string[];

      const cidades = new Set<string>();
      const batch = 1000;
      let from = 0;

      // PostgREST não tem DISTINCT direto; pagina e deduplica no cliente.
      // Custo é baixo: até ~milhares de linhas por estado, raramente mais.
      while (true) {
        const { data, error } = await supabase
          .from("clientes")
          .select("cidade")
          .eq("estado", uf)
          .not("cidade", "is", null)
          .neq("cidade", "")
          .order("cidade", { ascending: true })
          .range(from, from + batch - 1);

        if (error) throw error;

        const rows = (data || []) as Array<{ cidade: string | null }>;
        for (const r of rows) {
          if (r.cidade) cidades.add(r.cidade.trim());
        }
        if (rows.length < batch) break;
        from += batch;
      }

      return Array.from(cidades).sort((a, b) => a.localeCompare(b, "pt-BR"));
    },
    enabled: !!uf,
    staleTime: 60 * 60 * 1000, // 1 hora
    refetchOnWindowFocus: false,
  });
}
